import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bell, BellRing, Building2, CalendarDays, CheckCheck, ChevronLeft, ChevronRight, FileText, Image as ImageIcon, Mail, MapPin,
    Megaphone, Paperclip, Phone, RotateCw, Send, ShieldCheck, Star, Store, Tag, Trash2, UploadCloud, UserCheck, UserPlus, Users, X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import { onAdminRefresh } from '../../utils/adminSocket';
import Navbar from '../../components/Navbar';
import Select from '../../components/Select';
import MultiSelect from '../../components/MultiSelect';
import ConfirmDialog from '../../components/ConfirmDialog';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { RULES, checkFiles, uploadFailMessage } from '../../utils/uploadRules';
import './Notifications.css';

const SERVER_URL = (API.defaults?.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const fileUrl = path => (path && path.startsWith('/') ? `${SERVER_URL}${path}` : path);

const SEND_TO_OPTIONS = [
    { value: 'all', label: 'Everyone (members + service providers)' },
    { value: 'member', label: 'All members' },
    { value: 'service', label: 'All service providers' },
    { value: 'premium', label: 'Premium users only' },
    { value: 'free', label: 'Free users only' },
    { value: 'specific', label: 'Specific people (pick below)' },
];

const TYPE_META = {
    all: { Icon: Megaphone, label: 'Everyone' },
    member: { Icon: Users, label: 'Members' },
    service: { Icon: Store, label: 'Service providers' },
    premium: { Icon: Star, label: 'Premium' },
    free: { Icon: Tag, label: 'Free users' },
    specific: { Icon: UserCheck, label: 'Specific people' },
};

const MAX_FILES = 5;
const blankForm = { title: '', message: '', type: 'all', userIds: [] };
const publishUnread = count => window.dispatchEvent(new CustomEvent('adminAlertsChanged', { detail: { count } }));
const isImageAttachment = file => file?.kind === 'image' || /\.(avif|gif|jpe?g|png|webp)$/i.test(file?.name || file?.url || '');

const formatSize = bytes => (bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const timeAgo = date => {
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const NotificationAttachment = ({ file, index }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const isImage = isImageAttachment(file);
    const attachmentUrl = fileUrl(file.url);

    return (
        <a key={file.url || `${file.name}-${index}`} className={`history-card__file ${isImage ? 'is-image' : 'is-document'}`} href={attachmentUrl} target="_blank" rel="noreferrer" title={`Open ${file.name || 'attachment'}`}>
            {isImage && !imageFailed ? (
                <img src={attachmentUrl} alt={file.name || 'Notification attachment'} onError={() => setImageFailed(true)} />
            ) : isImage ? (
                <span className="history-card__image-fallback" role="img" aria-label="Image unavailable"><ImageIcon size={22} /></span>
            ) : (
                <span className="history-card__file-icon"><FileText size={18} /></span>
            )}
            <span className="history-card__file-name">
                {file.name || 'Attachment'}
                {file.size ? <small>{formatSize(file.size)}</small> : null}
            </span>
        </a>
    );
};

const Notifications = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState('activity');
    const [alerts, setAlerts] = useState([]);
    const [unread, setUnread] = useState(0);
    const [activityFilter, setActivityFilter] = useState('all');

    const [alertPage, setAlertPage] = useState(1);
    const [alertTotal, setAlertTotal] = useState(0);
    const [alertPages, setAlertPages] = useState(1);
    const alertQuery = useRef({ page: 1, filter: 'all' }); // read by the timer / socket, which must not see stale state
    const [pickedAlerts, setPickedAlerts] = useState([]);

    const [notifications, setNotifications] = useState([]);
    const [sentPage, setSentPage] = useState(1);
    const [sentTotal, setSentTotal] = useState(0);
    const [sentPages, setSentPages] = useState(1);
    const sentQuery = useRef({ page: 1 });
    const [pickedSent, setPickedSent] = useState([]);
    const [form, setForm] = useState(blankForm);
    const [files, setFiles] = useState([]);              // [{ file, preview? }]
    const [recipients, setRecipients] = useState([]);
    const [sending, setSending] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null); // { kind: 'alert' | 'sent', ids: [...] }
    const [deleting, setDeleting] = useState(false);
    const fileInput = useRef(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const prefillUserId = params.get('userId');
        if (prefillUserId) {
            setTab('send');
            handleSendToChange('specific');
            setForm(prev => ({
                ...prev,
                type: 'specific',
                userIds: [prefillUserId],
                title: params.get('title') || 'Profile Verification Needs Attention',
            }));
        }
    }, []);

    // 10 alerts per page, filtered and paged on the server
    const fetchAlerts = async (next) => {
        if (next) alertQuery.current = { ...alertQuery.current, ...next };
        const { page, filter } = alertQuery.current;
        try {
            const res = await API.get('/admin/alerts', { params: { page, limit: 10, filter } });
            const pages = res.data.totalPages || 1;
            if (page > pages) return fetchAlerts({ page: pages }); // the last page emptied out after a delete
            setAlerts(res.data.alerts || []);
            setUnread(res.data.unread || 0);
            setAlertTotal(res.data.total || 0);
            setAlertPages(pages);
            setAlertPage(page);
            setPickedAlerts(prev => prev.filter(id => (res.data.alerts || []).some(a => a._id === id)));
            publishUnread(res.data.unread || 0);
        } catch (err) { }
    };

    const fetchNotifications = async (next) => {
        if (next) sentQuery.current = { ...sentQuery.current, ...next };
        const { page } = sentQuery.current;
        try {
            const res = await API.get('/admin/notifications', { params: { page, limit: 10 } });
            const pages = res.data.totalPages || 1;
            if (page > pages) return fetchNotifications({ page: pages });
            setNotifications(res.data.notifications || []);
            setSentTotal(res.data.total || 0);
            setSentPages(pages);
            setSentPage(page);
            setPickedSent(prev => prev.filter(id => (res.data.notifications || []).some(n => n._id === id)));
        } catch (err) { toast.error('Failed to load'); }
    };

    const toggleId = (setter, id) => setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    useEffect(() => {
        fetchAlerts();
        fetchNotifications();
        const timer = setInterval(fetchAlerts, 10000); // new registrations appear without a refresh
        const offRefresh = onAdminRefresh(fetchAlerts); // and at once when the server says there is a new alert
        return () => { clearInterval(timer); offRefresh(); };
    }, []);

    // Free the temporary image previews when files change / page closes.
    useEffect(() => () => files.forEach(f => f.preview && URL.revokeObjectURL(f.preview)), [files]);

    const markRead = async (alert) => {
        if (alert.isRead) return;
        const nextUnread = Math.max(0, unread - 1);
        setAlerts(prev => prev.map(a => (a._id === alert._id ? { ...a, isRead: true } : a)));
        setUnread(nextUnread);
        publishUnread(nextUnread);
        try { await API.put(`/admin/alerts/${alert._id}/read`); } catch (err) { }
    };

    const markAllRead = async () => {
        try {
            await API.put('/admin/alerts/read-all');
            setAlerts(prev => prev.map(a => ({ ...a, isRead: true })));
            setUnread(0);
            publishUnread(0);
        } catch (err) { toast.error('Failed'); }
    };

    const handleSendToChange = async (type) => {
        setForm(prev => ({ ...prev, type, userIds: type === 'specific' ? prev.userIds : [] }));
        if (type === 'specific' && recipients.length === 0) {
            try {
                const res = await API.get('/admin/notification-recipients');
                setRecipients(res.data.recipients || []);
            } catch (err) { toast.error('Failed to load recipients'); }
        }
    };

    const addFiles = (list) => {
        const accepted = checkFiles(list, RULES.notice, { have: files.length, maxFiles: MAX_FILES });
        const next = [...files, ...accepted.map(file => ({ file, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : '' }))];
        setFiles(next);
        if (fileInput.current) fileInput.current.value = '';
    };

    const removeFile = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.type === 'specific' && form.userIds.length === 0) {
            toast.error('Pick at least one recipient');
            return;
        }
        setSending(true);
        try {
            const data = new FormData();
            data.append('title', form.title);
            data.append('message', form.message);
            data.append('type', form.type);
            data.append('userIds', JSON.stringify(form.userIds));
            files.forEach(f => data.append('files', f.file));
            const res = await API.post('/admin/notifications', data, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(res.data.message || 'Notification sent!');
            setForm(blankForm);
            setFiles([]);
            fetchNotifications();
        } catch (err) {
            toast.error(files.length && (err.response?.status === 413 || !err.response) ? uploadFailMessage(err, files.map(f => f.file), RULES.notice) : (err.response?.data?.message || 'Failed to send'));
        } finally {
            setSending(false);
        }
    };

    const confirmDelete = async () => {
        const job = pendingDelete;
        if (!job || !job.ids.length) return setPendingDelete(null);
        setDeleting(true);
        try {
            await API.delete(job.kind === 'alert' ? '/admin/alerts' : '/admin/notifications', { data: { ids: job.ids } });
            toast.success(job.ids.length === 1 ? 'Deleted' : `${job.ids.length} deleted`);
            if (job.kind === 'alert') { setPickedAlerts([]); fetchAlerts(); } else { setPickedSent([]); fetchNotifications(); }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not delete');
        } finally {
            setDeleting(false);
            setPendingDelete(null);
        }
    };

    const Pager = ({ page, pages, total, onPage }) => (
        <div className="notif-pager">
            <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}><ChevronLeft size={15} /> Previous</button>
            <span>Page {page} of {pages} · {total} total</span>
            <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next <ChevronRight size={15} /></button>
        </div>
    );

    const memberOptions = recipients.map(p => ({
        value: p._id,
        label: `${p.name || 'Unnamed'} · ${p.role === 'service' ? 'Service' : 'Member'}`,
        meta: p.mobile || '',
    }));

    const visibleAlerts = alerts; // already filtered and paged by the server

    return (
        <div className="notif-layout">
            <Sidebar />
            <div className="admin-content notif-content">
                <Navbar title={<span className="notif-nav-title"><Bell size={18} /> Notifications</span>} />

                <main className="notif-page">
                    <div className="notif-tabs" role="tablist">
                        <button type="button" role="tab" aria-selected={tab === 'activity'} className={tab === 'activity' ? 'is-active' : ''} onClick={() => setTab('activity')}>
                            <BellRing size={15} /> Activity
                            {unread > 0 && <span className="notif-tabs__badge">{unread}</span>}
                        </button>
                        <button type="button" role="tab" aria-selected={tab === 'send'} className={tab === 'send' ? 'is-active' : ''} onClick={() => setTab('send')}>
                            <Send size={15} /> Send notification
                        </button>
                    </div>

                    {tab === 'activity' && (
                        <section>
                            <div className="notif-toolbar">
                                <div>
                                    <h2>Activity & Registrations</h2>
                                    <p>{alertTotal} {alertTotal === 1 ? 'alert' : 'alerts'} · {unread} unread</p>
                                </div>
                                <div className="notif-toolbar__actions">
                                    <div className="notif-filters" role="group" aria-label="Filter activity">
                                        {[['all', 'All'], ['unread', 'Unread'], ['verification', 'Verifications'], ['member', 'Members'], ['service', 'Service providers']].map(([id, label]) => (
                                            <button key={id} type="button" className={activityFilter === id ? 'is-active' : ''} onClick={() => { setActivityFilter(id); setPickedAlerts([]); fetchAlerts({ page: 1, filter: id }); }}>{label}</button>
                                        ))}
                                    </div>
                                    <button type="button" className="notif-ghost" onClick={markAllRead} disabled={!unread}><CheckCheck size={15} /> Mark all read</button>
                                </div>
                            </div>

                            {visibleAlerts.length > 0 && (
                                <div className="notif-selectbar">
                                    <label><input type="checkbox" checked={pickedAlerts.length === visibleAlerts.length} onChange={event => setPickedAlerts(event.target.checked ? visibleAlerts.map(a => a._id) : [])} /> Select all on this page</label>
                                    {pickedAlerts.length > 0 && (
                                        <button type="button" className="notif-selectbar__delete" onClick={() => setPendingDelete({ kind: 'alert', ids: pickedAlerts })}><Trash2 size={14} /> Delete selected ({pickedAlerts.length})</button>
                                    )}
                                </div>
                            )}
                            <div className="notif-feed">
                                {visibleAlerts.map(a => {
                                    const isService = a.type === 'new_service';
                                    const isVerification = a.type === 'profile_verification_resubmitted';
                                    return (
                                        <article
                                            key={a._id}
                                            className={`activity-card${a.isRead ? '' : ' is-unread'}`}
                                            onClick={() => {
                                                markRead(a);
                                                if (isVerification) {
                                                    navigate(`/profiles?q=${encodeURIComponent(a.person?.mobile || a.person?.name || '')}&open=1`);
                                                }
                                            }}
                                        >
                                            <input type="checkbox" className="notif-pick" checked={pickedAlerts.includes(a._id)} onClick={event => event.stopPropagation()} onChange={() => toggleId(setPickedAlerts, a._id)} aria-label="Select this alert" />
                                            <span className={`activity-card__icon ${isVerification ? 'is-verification' : isService ? 'is-service' : 'is-member'}`}>
                                                {isVerification ? <RotateCw size={20} /> : isService ? <Building2 size={20} /> : <UserPlus size={20} />}
                                            </span>
                                            <div className="activity-card__body">
                                                <div className="activity-card__top">
                                                    <h3>{a.title}</h3>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {isVerification && (
                                                            <button
                                                                type="button"
                                                                className="activity-card__review-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    markRead(a);
                                                                    navigate(`/profiles?q=${encodeURIComponent(a.person?.mobile || a.person?.name || '')}&open=1`);
                                                                }}
                                                            >
                                                                <ShieldCheck size={13} /> Review Profile
                                                            </button>
                                                        )}
                                                        <span className="activity-card__time">{!a.isRead && <i className="notif-dot" />}{timeAgo(a.createdAt)}</span>
                                                        <button type="button" className="notif-trash" aria-label="Delete this alert" onClick={event => { event.stopPropagation(); setPendingDelete({ kind: 'alert', ids: [a._id] }); }}><Trash2 size={15} /></button>
                                                    </div>
                                                </div>
                                                <div className="activity-card__name">{a.person?.name || a.message}</div>
                                                {a.person?.detail && <div className="activity-card__detail">{a.person.detail}</div>}
                                                <div className="activity-card__info">
                                                    {a.person?.mobile && <span><Phone size={12} /> {a.person.mobile}</span>}
                                                    {a.person?.email && <span><Mail size={12} /> {a.person.email}</span>}
                                                    {a.person?.location && <span><MapPin size={12} /> {a.person.location}</span>}
                                                    <span><CalendarDays size={12} /> {new Date(a.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                                {visibleAlerts.length === 0 && (
                                    <div className="notif-empty">
                                        <span><BellRing size={26} /></span>
                                        <p>No registrations to show yet. New members and service providers appear here as they sign up.</p>
                                    </div>
                                )}
                            </div>
                            {alertTotal > 0 && <Pager page={alertPage} pages={alertPages} total={alertTotal} onPage={page => { setPickedAlerts([]); fetchAlerts({ page }); }} />}
                        </section>
                    )}

                    {tab === 'send' && (
                        <section className="notif-send">
                            <form className="notif-form" onSubmit={handleSubmit}>
                                <header>
                                    <span><Send size={18} /></span>
                                    <div>
                                        <h2>Send a notification</h2>
                                        <p>Reaches the notification bell of every person it targets, with any files you attach.</p>
                                    </div>
                                </header>

                                <label>Title
                                    <input placeholder="Notification title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required maxLength={120} />
                                </label>
                                <label>Message
                                    <textarea rows={4} placeholder="Notification message..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required maxLength={2000} />
                                </label>

                                <div className="notif-field">
                                    <span className="notif-field__label">Send to</span>
                                    <Select value={form.type} onChange={handleSendToChange} options={SEND_TO_OPTIONS} />
                                </div>

                                {form.type === 'specific' && (
                                    <div className="notif-field">
                                        <span className="notif-field__label">Recipients ({form.userIds.length} selected)</span>
                                        <MultiSelect
                                            value={form.userIds}
                                            onChange={userIds => setForm({ ...form, userIds })}
                                            options={memberOptions}
                                            placeholder="Select members or service providers"
                                            searchPlaceholder="Search by name..."
                                        />
                                    </div>
                                )}

                                <div className="notif-field">
                                    <span className="notif-field__label"><Paperclip size={13} /> Attachments <em>images or documents · up to {MAX_FILES} files · 10MB each</em></span>
                                    <div
                                        className="notif-drop"
                                        onClick={() => fileInput.current?.click()}
                                        onDragOver={e => e.preventDefault()}
                                        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                                    >
                                        <UploadCloud size={24} />
                                        <strong>Click to upload or drag files here</strong>
                                        <small>JPG, PNG, PDF, Word, Excel, PowerPoint, TXT</small>
                                        <input ref={fileInput} type="file" multiple hidden
                                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                                            onChange={e => addFiles(e.target.files)} />
                                    </div>

                                    {files.length > 0 && (
                                        <ul className="notif-files">
                                            {files.map((f, i) => (
                                                <li key={`${f.file.name}-${i}`}>
                                                    {f.preview ? <img src={f.preview} alt="" /> : <span className="notif-files__doc"><FileText size={18} /></span>}
                                                    <span className="notif-files__name">{f.file.name}<small>{formatSize(f.file.size)}</small></span>
                                                    <button type="button" onClick={() => removeFile(i)} aria-label={`Remove ${f.file.name}`}><X size={14} /></button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <button type="submit" className="notif-submit" disabled={sending}>
                                    <Bell size={16} /> {sending ? 'Sending…' : 'Send notification'}
                                </button>
                            </form>

                            <div className="notif-history">
                                <h2>Sent notifications <span>{sentTotal}</span></h2>
                                {notifications.length > 0 && (
                                    <div className="notif-selectbar">
                                        <label><input type="checkbox" checked={pickedSent.length === notifications.length} onChange={event => setPickedSent(event.target.checked ? notifications.map(n => n._id) : [])} /> Select all on this page</label>
                                        {pickedSent.length > 0 && (
                                            <button type="button" className="notif-selectbar__delete" onClick={() => setPendingDelete({ kind: 'sent', ids: pickedSent })}><Trash2 size={14} /> Delete selected ({pickedSent.length})</button>
                                        )}
                                    </div>
                                )}
                                {notifications.map(n => {
                                    const meta = TYPE_META[n.type] || TYPE_META.all;
                                    return (
                                        <article key={n._id} className="history-card">
                                            <input type="checkbox" className="notif-pick" checked={pickedSent.includes(n._id)} onChange={() => toggleId(setPickedSent, n._id)} aria-label="Select this notification" />
                                            <span className="history-card__icon"><meta.Icon size={18} /></span>
                                            <div className="history-card__body">
                                                <h3>{n.title}</h3>
                                                <p>{n.message}</p>
                                                {n.attachments?.length > 0 && (
                                                    <div className="history-card__files">
                                                        {n.attachments.map((file, index) => <NotificationAttachment key={file.url || `${file.name}-${index}`} file={file} index={index} />)}
                                                    </div>
                                                )}
                                                <div className="history-card__meta">
                                                    <span className="notif-chip"><meta.Icon size={11} /> {meta.label}</span>
                                                    {n.recipientCount > 0 && <span className="notif-chip"><Users size={11} /> {n.recipientCount} {n.recipientCount === 1 ? 'recipient' : 'recipients'}</span>}
                                                    <span className="history-card__date"><CalendarDays size={11} /> {new Date(n.createdAt).toLocaleDateString('en-IN')}</span>
                                                </div>
                                            </div>
                                            <button type="button" className="history-card__delete" onClick={() => setPendingDelete({ kind: 'sent', ids: [n._id] })} aria-label="Delete notification"><Trash2 size={15} /></button>
                                        </article>
                                    );
                                })}
                                {notifications.length === 0 && (
                                    <div className="notif-empty">
                                        <span><Bell size={26} /></span>
                                        <p>No notifications sent yet</p>
                                    </div>
                                )}
                                {sentTotal > 0 && <Pager page={sentPage} pages={sentPages} total={sentTotal} onPage={page => { setPickedSent([]); fetchNotifications({ page }); }} />}
                            </div>
                        </section>
                    )}
                </main>
            </div>

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title={pendingDelete?.ids.length > 1 ? `Delete ${pendingDelete.ids.length} items?` : (pendingDelete?.kind === 'alert' ? 'Delete this alert?' : 'Delete notification?')}
                message={pendingDelete?.kind === 'alert' ? 'This removes it from the activity list for good.' : 'This removes it from the sent list. People who already received it will still see it in their bell.'}
                confirmLabel={deleting ? 'Deleting…' : 'Delete'}
                danger
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

export default Notifications;
